import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const de = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '');
const eur = (v) => `${(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const senden = (k) =>
  axios.post(`${API}/rechnungen/${k.rechnung_id}/mahnung/1/send-email`, {
    betrag: k.betrag, beschreibung: k.beschreibung || '',
  });

const Zeile = ({ k, busy, onSend }) => (
  <tr className="border-b border-[#F1E8DF] last:border-0" data-testid={`m1-row-${k.rechnungsnummer}`}>
    <td className="py-2 pr-3 font-mono text-[13px]">{k.rechnungsnummer}</td>
    <td className="py-2 pr-3">
      <div>{k.empfaenger_name}</div>
      <div className="text-[11px] text-[#8E7B71]" data-testid={`m1-email-${k.rechnungsnummer}`}>{k.to_email || 'keine E-Mail hinterlegt'}</div>
    </td>
    <td className="py-2 pr-3 text-[12px] text-[#8E7B71]">{de(k.erinnerung_am)}</td>
    <td className="py-2 pr-3 text-right tabular-nums font-semibold" data-testid={`m1-betrag-${k.rechnungsnummer}`}>
      {eur(k.betrag)}
      {k.beschreibung && <div className="text-[10px] font-normal text-[#8E7B71]">{k.beschreibung}</div>}
      {k.zahlung_seit_erinnerung > 0 && <div className="text-[10px] font-normal text-[#6E866F]">seit Erinnerung gezahlt: {eur(k.zahlung_seit_erinnerung)}</div>}
    </td>
    <td className="py-2 pl-2 text-right">
      {onSend && (
        <button onClick={() => onSend(k)} disabled={busy || !k.to_email} data-testid={`m1-send-${k.rechnungsnummer}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#E07856] px-3 py-1 text-xs text-white transition-opacity hover:opacity-90 disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} 1. Mahnung
        </button>
      )}
      {!onSend && <span className="text-[11px] text-[#8E7B71]">ab {de(k.mahnstufe1_ab)}</span>}
    </td>
  </tr>
);

const Tabelle = ({ rows, busy, onSend, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm text-[#3D3835]">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#8E7B71] border-b border-[#E0D6CA]">
          <th className="py-2 pr-3">Rechnung</th><th className="py-2 pr-3">Empfänger</th>
          <th className="py-2 pr-3">Erinnert am</th><th className="py-2 pr-3 text-right">Anzumahnen</th><th />
        </tr>
      </thead>
      <tbody>{rows.map((k) => <Zeile key={k.rechnung_id} k={k} busy={busy === k.rechnung_id || busy === 'alle'} onSend={onSend} />)}</tbody>
    </table>
  </div>
);

export const MahnstufeEinsWidget = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [zeigeWartend, setZeigeWartend] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/mahnwesen/stufe1-kandidaten`).then((r) => setData(r.data)).catch(() => setData({ bereit: [], wartend: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const einzeln = async (k) => {
    if (!window.confirm(`1. Mahnung über ${eur(k.betrag)} an ${k.to_email} senden?`)) return;
    setBusy(k.rechnung_id);
    try { await senden(k); toast.success(`1. Mahnung ${k.rechnungsnummer} gesendet`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
    setBusy(null);
  };

  const alle = async () => {
    const rows = data.bereit.filter((k) => k.to_email);
    if (!window.confirm(`${rows.length} Mahnungen (1. Mahnung, Frist laut Mahnregel) über ${eur(rows.reduce((s, k) => s + k.betrag, 0))} jetzt senden?`)) return;
    setBusy('alle');
    let ok = 0;
    for (const k of rows) {
      try { await senden(k); ok += 1; } catch (e) { toast.error(`${k.rechnungsnummer}: ${e.response?.data?.detail || 'Fehler'}`); }
    }
    toast.success(`${ok} von ${rows.length} Mahnungen gesendet`);
    setBusy(null); load();
  };

  if (!data) return null;
  return (
    <div className="mt-8 rounded-lg border border-[#E0D6CA] bg-white p-5" data-testid="mahnstufe1-widget">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#E07856]">Wartezeit nach Zahlungserinnerung abgelaufen</div>
          <h3 className="mt-0.5 text-xl text-[#3D3835]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>Mahnstufe 1 – bereit zum Versand</h3>
        </div>
        {data.bereit.length > 0 && (
          <button onClick={alle} disabled={!!busy} data-testid="m1-send-all"
            className="rounded-full bg-[#3D3835] px-4 py-1.5 text-xs text-white transition-opacity hover:opacity-90 disabled:opacity-40">
            Alle {data.bereit.length} senden ({eur(data.summe_bereit)})
          </button>
        )}
      </div>
      {data.bereit.length === 0
        ? <div className="rounded-md border border-[#D3DFD1] bg-[#E9EFE7] p-4 text-sm text-[#4F6650]" data-testid="m1-leer">{data.heute_versandtag === false ? `Heute ist kein Versandtag – Mahnungen gehen nur mittwochs und freitags raus (nächster: ${de(data.naechster_versandtag)}).` : "Aktuell ist keine 1. Mahnung fällig."}</div>
        : <Tabelle rows={data.bereit} busy={busy} onSend={einzeln} testid="m1-bereit-tabelle" />}
      {data.wartend.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setZeigeWartend(!zeigeWartend)} className="text-xs uppercase tracking-wider text-[#8E7B71] hover:text-[#3D3835]" data-testid="m1-toggle-wartend">
            {zeigeWartend ? 'Ausblenden' : `${data.wartend.length} Erinnerungen warten (Frist + 2 Werktage Puffer, Versand nur Mi/Fr)`}
          </button>
          {zeigeWartend && <Tabelle rows={data.wartend} testid="m1-wartend-tabelle" />}
        </div>
      )}
    </div>
  );
};
